<template>
  <div class="about-container">
    <!-- 使用公共导航栏组件 -->
    <Nav />

    <!-- 主内容区域 -->
    <div class="max-w-4xl mx-auto px-4 py-8 mt-20">
      <!-- 页面标题 -->
      <div class="flex items-center mb-8">
        <button @click="goBack" class="text-gray-500 hover:text-gray-700 mr-4">
          <i class="fas fa-arrow-left"></i>
        </button>
        <h1 class="text-2xl font-bold text-gray-800">订单支付</h1>
      </div>

      <!-- 支付内容区域 -->
      <div
        class="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6"
      >
        <div class="space-y-6">
          <!-- 订单信息 -->
          <div class="border-b pb-4">
            <h2 class="text-lg font-semibold text-gray-800 mb-3">订单信息</h2>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <span class="text-gray-500">订单编号：</span>
                <span class="text-gray-800">{{
                  orderData.order_number || "载入中..."
                }}</span>
              </div>
              <div>
                <span class="text-gray-500">创建时间：</span>
                <span class="text-gray-800">{{
                  this.formatDate(
                    orderData.createtime,
                    "YYYY-MM-DD HH:mm:ss"
                  ) || "载入中..."
                }}</span>
              </div>
            </div>
          </div>

          <!-- 收货信息 -->
          <div class="border-b pb-4">
            <h2 class="text-lg font-semibold text-gray-800 mb-3">收货信息</h2>
            <div class="flex flex-col space-y-2">
              <div v-if="orderData.recipient_name">
                <span class="text-gray-500">收货人：</span>
                <span class="text-gray-800">{{
                  orderData.recipient_name || "-"
                }}</span>
                <span class="text-gray-800 ml-4">{{
                  orderData.recipient_phone || "-"
                }}</span>
              </div>
              <div v-if="orderData.recipient_address">
                <span class="text-gray-500">收货地址：</span>
                <span class="text-gray-800">{{
                  orderData.recipient_address || "-"
                }}</span>
              </div>
              <div v-else>
                <span class="text-gray-500">暂无收货信息</span>
              </div>
            </div>
          </div>

          <!-- 支付金额 -->
          <div class="flex justify-between items-center">
            <span class="text-lg text-gray-700">应付金额：</span>
            <span class="text-2xl font-bold text-red-500"
              >¥{{ orderData.settlement_amount || "0.00" }}</span
            >
          </div>

          <!-- 支付方式选择 -->
          <div>
            <h2 class="text-lg font-semibold text-gray-800 mb-3">支付方式</h2>
            <div class="flex space-x-4">
              <div
                @click="selectPaymentMethod('alipay')"
                :class="[
                  'flex-1 border rounded-lg p-4 flex items-center cursor-pointer transition-all',
                  selectedPayment === 'alipay'
                    ? 'border-primary bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300',
                ]"
              >
                <div class="mr-3">
                  <i class="ri-alipay-fill text-blue-500 text-lg"></i>
                </div>
                <div class="flex-1">
                  <div class="font-medium">支付宝</div>
                </div>
                <div
                  :class="[
                    'w-5 h-5 rounded-full border-2 flex items-center justify-center',
                    selectedPayment === 'alipay'
                      ? 'border-primary'
                      : 'border-gray-300',
                  ]"
                >
                  <div
                    v-if="selectedPayment === 'alipay'"
                    class="w-3 h-3 rounded-full bg-primary"
                  ></div>
                </div>
              </div>

              <div
                @click="selectPaymentMethod('wechat')"
                :class="[
                  'flex-1 border rounded-lg p-4 flex items-center cursor-pointer transition-all',
                  selectedPayment === 'wechat'
                    ? 'border-primary bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300',
                ]"
              >
                <div class="mr-3">
                  <i class="ri-wechat-pay-fill text-green-500 text-lg"></i>
                </div>
                <div class="flex-1">
                  <div class="font-medium">微信支付</div>
                </div>
                <div
                  :class="[
                    'w-5 h-5 rounded-full border-2 flex items-center justify-center',
                    selectedPayment === 'wechat'
                      ? 'border-primary'
                      : 'border-gray-300',
                  ]"
                >
                  <div
                    v-if="selectedPayment === 'wechat'"
                    class="w-3 h-3 rounded-full bg-primary"
                  ></div>
                </div>
              </div>

              <!-- 新增银行转账选项 -->
              <div
                @click="selectPaymentMethod('bank_transfer')"
                :class="[
                  'flex-1 border rounded-lg p-4 flex items-center cursor-pointer transition-all',
                  selectedPayment === 'bank_transfer'
                    ? 'border-primary bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300',
                ]"
              >
                <div class="mr-3">
                  <i class="ri-bank-card-line text-gray-600 text-md"></i>
                </div>
                <div class="flex-1">
                  <div class="font-medium">银行转账</div>
                </div>
                <div
                  :class="[
                    'w-5 h-5 rounded-full border-2 flex items-center justify-center',
                    selectedPayment === 'bank_transfer'
                      ? 'border-primary'
                      : 'border-gray-300',
                  ]"
                >
                  <div
                    v-if="selectedPayment === 'bank_transfer'"
                    class="w-3 h-3 rounded-full bg-primary"
                  ></div>
                </div>
              </div>
            </div>
          </div>

          <!-- 银行转账账户信息 (当选择银行转账时显示) -->
          <div
            v-if="selectedPayment === 'bank_transfer'"
            class="mt-6 bg-gray-50 p-4 rounded-lg border border-gray-200"
          >
            <h3 class="text-md font-semibold text-gray-800 mb-3">
              公司收款账户信息
            </h3>
            <div class="space-y-3">
              <div class="flex">
                <span class="text-gray-500 w-24">开户银行：</span>
                <span class="text-gray-800 font-medium">中国银行</span>
              </div>
              <div class="flex">
                <span class="text-gray-500 w-24">账户名称：</span>
                <span class="text-gray-800 font-medium"
                  >会找我科技有限公司</span
                >
              </div>
              <div class="flex">
                <span class="text-gray-500 w-24">银行账号：</span>
                <span class="text-gray-800 font-medium"
                  >6222 6666 8888 8888</span
                >
              </div>
              <div class="mt-3 text-sm text-gray-500">
                <p>
                  请在转账时备注您的订单编号：{{ orderData.order_number || "" }}
                </p>
                <p class="mt-1">转账完成后，请联系客服确认支付状态</p>
              </div>
            </div>
          </div>

          <!-- 微信支付二维码显示区域 -->
          <div
            class="mt-8 flex flex-col items-center"
            v-if="showWechatPayQrCode && selectedPayment === 'wechat'"
          >
            <h3 class="text-lg font-semibold text-gray-800 mb-4">
              微信扫码支付
            </h3>
            <div class="border border-gray-200 p-4 rounded-lg bg-white">
              <!-- 二维码图片 -->
              <div class="flex justify-center items-center w-48 h-48 mb-4">
                <img
                  v-if="wechatPayQrCode"
                  :src="wechatPayQrCode"
                  alt="微信支付二维码"
                  class="max-w-full max-h-full"
                />
                <div v-else class="text-gray-500">二维码加载中...</div>
              </div>
              <!-- 提示信息 -->
              <div class="text-center">
                <p class="text-gray-600">请使用微信扫一扫支付</p>
              </div>
            </div>
            <div class="mt-4 text-sm text-gray-500">
              <p>支付完成后会自动跳转页面</p>
            </div>
            <div class="mt-4 text-sm text-gray-600">
              <p>
                支付倒计时: <span class="countdown">{{ countdownSeconds }}</span
                >秒
              </p>
            </div>
          </div>

          <!-- 发起支付按钮 (根据选择的支付方式显示) -->
          <div class="flex flex-col items-center justify-center w-full">
            <!-- 初始支付按钮 -->
            <div
              v-if="
                ['alipay', 'wechat'].includes(selectedPayment) &&
                !showWechatPayQrCode
              "
              class="w-full"
            >
              <button
                class="py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium text-lg w-full"
                :disabled="paying"
                @click="initiatePayment"
                style="width: 100% !important; min-width: 200px"
              >
                {{
                  paying
                    ? "数据载入中..."
                    : `立即${
                        selectedPayment === "alipay" ? "支付宝" : "微信"
                      }支付`
                }}
              </button>
              <div class="text-sm text-gray-500 mt-4 text-center">
                {{
                  selectedPayment === "alipay"
                    ? "点击按钮后将跳转到支付宝支付页面"
                    : "点击按钮后显示微信支付二维码"
                }}
              </div>
            </div>

            <!-- 微信支付码显示时的按钮组 -->
            <div
              v-else-if="showWechatPayQrCode && selectedPayment === 'wechat'"
              class="flex space-x-4 mt-4"
            >
              <button
                class="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                @click="
                  showWechatPayQrCode = false;
                  clearPaymentPolling();
                "
              >
                重新选择支付方式
              </button>
              <button
                class="px-6 py-2 border border-primary text-primary rounded-lg hover:bg-blue-50 transition-colors"
                :disabled="paying"
                @click="initiatePayment"
              >
                {{ paying ? "处理中..." : "刷新二维码" }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 使用公共页脚组件 -->
    <Footer />
  </div>
</template>

<script>
import Nav from "@/components/Nav.vue";
import Footer from "@/components/Footer.vue";
import api from "@/utils/api.js";
import { formatDate } from "@/utils/tool.js";

export default {
  name: "Cashier",
  components: {
    Nav,
    Footer,
  },
  data() {
    return {
      // 订单数据
      orderData: {},
      // 选中的支付方式
      selectedPayment: "",

      // 支付处理中状态
      paying: false,

      // 微信支付相关
      wechatPayQrCode: "", // 微信支付二维码URL
      showWechatPayQrCode: false, // 是否显示微信支付二维码
      paymentPollingTimer: null, // 支付状态轮询定时器
      countdownSeconds: 120, // 倒计时秒数
      countdownTimer: null, // 倒计时定时器
    };
  },
  mounted() {
    // 默认从loadOrderDetail方法获取订单信息
    this.loadOrderDetail();
  },
  methods: {
    // 加载订单详情
    async loadOrderDetail() {
      const orderNumber = this.$route.params.orderNumber;
      if (!orderNumber) {
        this.$message.error("订单编号不存在");
        this.$router.push("/index");
        return;
      }

      this.paying = true;

      try {
        const response = await api.order.getOrderDetail({
          order_number: orderNumber,
        });
        this.orderData = response.data;

        console.log("订单详情:", this.orderData);


      } catch (error) {
        console.error("获取订单详情失败:", error);
      } finally {
        this.paying = false;
      }
    },

    // 返回上一页
    goBack() {
      this.$router.back();
    },

    // 格式化日期时间
    formatDate(date, format = "YYYY-MM-DD") {
      return formatDate(date, format);
    },

    // 选择支付方式
    selectPaymentMethod(method) {
      // 无论切换到哪种支付方式，都重置二维码显示状态
      this.showWechatPayQrCode = false;
      // 清除可能存在的支付轮询
      this.clearPaymentPolling();

      this.selectedPayment = method;
      // 直接触发支付流程
      this.processPayment();
    },

    // 处理支付
    async processPayment() {
      if (!this.selectedPayment || this.paying) return;

      this.paying = true;

      try {
        // 模拟API调用延迟
        await new Promise((resolve) => setTimeout(resolve, 1000));

        if (this.selectedPayment === "bank_transfer") {
          // 银行转账 - 显示提示信息
          this.$message.success("请按照上方账户信息进行转账，并备注订单编号");
        }
      } catch (error) {
        console.error("处理支付失败:", error);
        this.$message.error("处理支付请求失败，请重试");
      } finally {
        this.paying = false;
      }
    },

    // 发起支付
    async initiatePayment() {
      if (!this.selectedPayment || this.paying) return;

      this.paying = true;

      try {
        // 根据支付方式构建不同的支付参数
        const paymentData = {
          order_number: this.orderData.order_number,
          payment_method: this.selectedPayment,
          settlement_amount: this.orderData.settlement_amount,
        };

        // 调用API发起支付
        const response = await api.payment.initiatePayment(paymentData);

        // 处理微信支付JSON响应
        if (this.selectedPayment === "wechat") {
          // 使用在线二维码生成服务生成二维码图片URL
          this.wechatPayQrCode = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(
            response.data.code_url
          )}`;
          // 显示二维码
          this.showWechatPayQrCode = true;

          // 开始轮询支付状态
          this.startPaymentPolling();
        }
        // 处理支付宝支付响应
        else if (this.selectedPayment === "alipay") {
          // 创建一个新窗口用于支付
          const paymentWindow = window.open("", "_blank");

          if (typeof response === "string" && response.includes("<form")) {
            // 创建临时div来存放HTML
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = response;

            // 找到表单元素
            const form = tempDiv.querySelector("form");
            if (form) {
              // 设置表单在新窗口提交
              form.setAttribute("target", "_blank");

              // 在新窗口中写入表单HTML
              if (paymentWindow) {
                paymentWindow.document.write(response);
                paymentWindow.document.close();

                // 自动提交表单
                setTimeout(() => {
                  const newWindowForm =
                    paymentWindow.document.querySelector("form");
                  if (newWindowForm) {
                    newWindowForm.submit();
                  }
                }, 100);

                // 启动轮询支付状态
                this.startPaymentPolling();
              } else {
                this.$message.error("无法打开支付窗口，请检查浏览器设置");
              }
            } else {
              this.$message.error("支付表单解析失败");
            }
          } else {
            this.$message.error("支付宝支付请求格式不正确");
          }
        }
        // 处理其他HTML响应
        else if (typeof response === "string") {
          // 处理自动跳转HTML
          if (response.includes('meta http-equiv="refresh"')) {
            // 创建临时div来存放HTML
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = response;

            // 尝试从meta标签提取跳转URL
            const metaRefresh = tempDiv.querySelector(
              "meta[http-equiv='refresh']"
            );
            if (metaRefresh && metaRefresh.getAttribute("content")) {
              const content = metaRefresh.getAttribute("content");
              const urlMatch = content.match(/url=['"](.*?)['"]/);
              if (urlMatch && urlMatch[1]) {
                const redirectUrl = urlMatch[1];
                // 直接跳转到支付页面
                window.location.href = redirectUrl;
                return;
              }
            }

            // 尝试从a标签提取跳转URL
            const redirectLink = tempDiv.querySelector("#redirect a");
            if (redirectLink && redirectLink.getAttribute("href")) {
              window.location.href = redirectLink.getAttribute("href");
              return;
            }
          }
        }
      } catch (error) {
        console.error("发起支付失败:", error);
      } finally {
        this.paying = false;
      }
    },

    // 开始轮询支付状态
    startPaymentPolling() {
      // 清除之前可能存在的定时器
      this.clearPaymentPolling();

      // 重置倒计时
      this.countdownSeconds = 120;

      // 启动倒计时定时器
      this.countdownTimer = setInterval(() => {
        if (this.countdownSeconds > 0) {
          this.countdownSeconds--;
        } else {
          // 倒计时结束，清除定时器并提示用户
          clearInterval(this.countdownTimer);
          this.countdownTimer = null;
          this.$message.warning("支付超时，请刷新二维码或重新选择支付方式");
        }
      }, 1000);

      // 设置新的轮询定时器，每3秒检查一次支付状态 离开页面时清除定时器
      this.paymentPollingTimer = setInterval(async () => {
        try {
          // 调用API检查支付状态
          const statusResponse = await api.payment.verifyPayment({
            order_number: this.orderData.order_number,
          });

          if (statusResponse.data) {
            // 清除轮询定时器
            this.clearPaymentPolling();

            // 显示成功提示
            this.$message.success("支付成功！正在跳转...");

            // 延迟跳转到订单详情页
            setTimeout(() => {
              this.$router.push({
                path: "/payment-result",
                query: {
                  status: "success",
                  orderNumber: this.orderData.order_number,
                  amount: this.orderData.settlement_amount,
                },
              });
            }, 1500);
          }
        } catch (error) {
          console.error("检查支付状态失败:", error);
          // 静默失败，继续轮询
        }
      }, 3000);
    },

    // 清除支付状态轮询定时器
    clearPaymentPolling() {
      if (this.paymentPollingTimer) {
        clearInterval(this.paymentPollingTimer);
        this.paymentPollingTimer = null;
      }

      // 清除倒计时定时器
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },

    // 组件销毁前清理定时器
    beforeUnmount() {
      this.clearPaymentPolling();
    },

    // 离开页面时清除定时器
    beforeDestroy() {
      this.clearPaymentPolling();
    },
  },
};
</script>

<style scoped>
/* 容器样式 */
.about-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding-bottom: 120px; /* 为底部固定操作栏留出空间 */
}
</style>



// WEBPACK FOOTER //
// src/pages/order/Cashier.vue